import { FetchRequest, FetchResetStatus } from 'acscp/actions';
import { FormattedMessage } from 'react-intl';
import { Dispatch } from 'redux';
import { AppState } from 'acscp/store';
import { defineMessages, InjectedIntlProps, injectIntl } from 'react-intl';
import React from 'react';
import { RequestStateProps } from 'acscp/components/common/helpers/requestStateProps';
import { Form } from 'acscp/components/common/form';
import formStyle from 'acscp/components/common/form.module.scss';
import buttonStyle from 'acscp/components/common/buttons.module.scss';
import { InputWithValidation } from 'acscp/components/common/validation/inputWithValidation';
import { ButtonWithLoadingIndicator } from 'acscp/components/common/buttonWithLoadingIndicator';
import { FormValidator, ValidationRule } from 'acscp/components/common/helpers/formValidator';
import { StringValidationChecks } from 'acscp/components/common/helpers/validationChecks';
import { connectWithRequests } from 'acscp/store/helpers/connectWithRequests';
import { RequestClass, VerifyAccountTokenRequest } from 'acscp/api/requests';
import { Link } from 'react-router-dom';
import { RequestErrorMessage } from 'acscp/components/common/requestErrorMessage';
import { RequestVerificationTokenRequest } from 'acscp/api/requests/requestVerificationTokenRequest';
import { home } from 'acscp/components/routeHelper';

const messages = defineMessages({
    emailError: {
        id: 'components.auth.verifyAccountForm.emailInvalid',
        defaultMessage: 'Please enter a valid email.'
    },
    tokenError: {
        id: 'components.auth.verifyAccountForm.tokenRequired',
        defaultMessage: 'Code is required.'
    },
    emailPlaceholder: {
        id: 'components.auth.verifyAccountForm.emailPlaceholder',
        defaultMessage: 'EMAIL'
    },
    tokenPlaceholder: {
        id: 'components.auth.verifyAccountForm.tokenPlaceholder',
        defaultMessage: 'CODE'
    }
});

interface VerifyAccountFormOwnProps {
    email?: string;
    token?: string;
    successButtonDestination?: string;
}

interface VerifyAccountFormStateProps {
    requestState: RequestStateProps;
    newTokenRequestState: RequestStateProps;
}

interface VerifyAccountFormDispatchProps {
    submitVerificationToken: (username: string, token: string) => void;
    resetSubmitErrors: () => void;
    requestNewToken: (username: string) => void;
}

interface VerifyAccountForm {
    username: string;
    tokenInput: string;
}

type VerifyAccountFormInternalState = VerifyAccountForm & {
    showErrors: boolean;
};

type VerifyAccountFormProps = InjectedIntlProps & VerifyAccountFormOwnProps & VerifyAccountFormStateProps
    & VerifyAccountFormDispatchProps;

export class VerifyAccountFormPresentational
    extends React.Component<VerifyAccountFormProps, VerifyAccountFormInternalState> {

    private validator: FormValidator;

    constructor(props: VerifyAccountFormProps) {
        super(props);

        this.validator = new FormValidator(this.getValidationRules());

        this.state = { username: this.props.email || '', tokenInput: this.props.token || '', showErrors: false };
    }

    componentDidMount(): void {
        if (this.props.email && this.props.token) {
            this.submitVerificationToken();
        }
    }

    private getValidationRules(): ValidationRule[] {
        const translator = this.props.intl.formatMessage;

        return [
            {
                validatedPropertyName: 'username',
                validationCheck: StringValidationChecks.isEmail(() => this.state.username),
                errorMessage: translator(messages.emailError)
            },
            {
                validatedPropertyName: 'tokenInput',
                validationCheck: StringValidationChecks.isRequired(() => this.state.tokenInput),
                errorMessage: translator(messages.tokenError)
            }
        ];
    }

    usernameChanged = (event: React.ChangeEvent<HTMLInputElement>) =>
        this.setState({ username: event.target.value })

    tokenChanged = (event: React.ChangeEvent<HTMLInputElement>) =>
        this.setState({ tokenInput: event.target.value })

    submitVerificationToken = () => {
        this.props.resetSubmitErrors();

        if (!this.props.requestState.isLoading) {
            const { username, tokenInput } = this.state;

            if (this.validator.isStateValid()) {
                // User might input the token with spaces
                const token = tokenInput.replace(/\W/g, '');

                this.props.submitVerificationToken(username, token);
            }
        }

        this.setState({ showErrors: true });
    }

    render(): JSX.Element {
        return (
            <Form
                submit={this.submitVerificationToken}
                header={
                    <FormattedMessage
                        id={'components.auth.verifyAccountForm.header'}
                        defaultMessage={'VERIFY ACCOUNT'}
                    />
                }
            >
                {this.props.requestState.success ?
                    this.renderSuccess() :
                    this.props.requestState.isLoading ? this.renderLoading() : this.renderFormContent()}
            </Form>
        );
    }

    renderFormContent(): JSX.Element {
        const { tokenInput, username } = this.state;
        const { isLoading, requestError } = this.props.requestState;
        const intl = this.props.intl;

        const validationErrors = this.validator.getDisplayedErrors(this.state.showErrors, requestError);

        return (
            <>
                <InputWithValidation
                        type="email"
                        name="username"
                        id="username"
                        value={username}
                        placeholder={intl.formatMessage(messages.emailPlaceholder)}
                        onChange={this.usernameChanged}
                        validationErrors={validationErrors}
                        autoFocus={true}
                />
                <InputWithValidation
                    type="text"
                    name="tokenInput"
                    id="tokenInput"
                    value={tokenInput}
                    placeholder={intl.formatMessage(messages.tokenPlaceholder)}
                    onChange={this.tokenChanged}
                    validationErrors={validationErrors}
                />
                <ButtonWithLoadingIndicator isLoading={isLoading}>
                    <FormattedMessage
                        id={'components.auth.verifyAccountForm.submitButton'}
                        defaultMessage={'SUBMIT'}
                    />
                </ButtonWithLoadingIndicator>
                <RequestErrorMessage requestState={this.props.requestState} />
                {!this.props.newTokenRequestState.success && !!requestError && <ButtonWithLoadingIndicator
                    isLoading={this.props.newTokenRequestState.isLoading}
                    onClick={() => this.props.requestNewToken(username)}
                >
                    <FormattedMessage
                        id={'components.auth.verifyAccountForm.requestNewToken'}
                        defaultMessage={'REQUEST NEW TOKEN'}
                    />
                </ButtonWithLoadingIndicator>}
                {this.props.newTokenRequestState.success &&
                    <div>
                        <FormattedMessage
                            id="components.auth.verifyAccountForm.requestNewTokenSuccess"
                            defaultMessage="If your email address is recognised a new verification email will be sent."
                        />
                    </div>
                }
                <Link to="/login" className={formStyle.TextLink}>
                    <FormattedMessage id={'components.auth.verifyAccountForm.back'} defaultMessage={'Back'} />
                </Link>
            </>
        );
    }

    renderSuccess(): JSX.Element {
        return (
            <>
                <div>
                    <FormattedMessage
                        id="components.auth.verifyAccountForm.success"
                        defaultMessage="Your email address has been successfully verified."
                    />
                </div>
                <Link to={this.props.successButtonDestination || home()} className={buttonStyle.BasicButton}>
                    <FormattedMessage
                        id={'components.auth.verifyAccountForm.continue'}
                        defaultMessage={'Continue to JetPortal'}
                    />
                </Link>
            </>
        );
    }

    renderLoading(): JSX.Element {
        return (
            <>
                <div>
                    <FormattedMessage
                        id="components.auth.verifyAccountForm.loading"
                        defaultMessage="We are verifying your email address."
                    />
                </div>
                <ButtonWithLoadingIndicator isLoading={true} />
            </>
        );
    }
}

function mapStateToProps(
    state: AppState, ownProps: {}, getRequestError: (request: RequestClass<any>) => RequestStateProps
): VerifyAccountFormStateProps {
    return {
        requestState: getRequestError(VerifyAccountTokenRequest),
        newTokenRequestState: getRequestError(RequestVerificationTokenRequest),
    };
}

function mapDispatchToProps(dispatch: Dispatch, ownProps: {}): VerifyAccountFormDispatchProps {
    return {
        submitVerificationToken: (username: string, token: string) =>
            dispatch(new FetchRequest(new VerifyAccountTokenRequest(username, token))),
        resetSubmitErrors: () => dispatch(new FetchResetStatus(VerifyAccountTokenRequest)),
        requestNewToken: (username: string) =>
            dispatch(new FetchRequest(new RequestVerificationTokenRequest(username)))
    };
}

export const VerifyAccountForm =
    connectWithRequests(mapStateToProps, mapDispatchToProps)(injectIntl(VerifyAccountFormPresentational));
